import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Lock } from 'lucide-react';
import { getRequiredPlan } from '@/utils/features';

/**
 * Locked Feature Button - Shows a disabled button with upgrade prompt
 */
export function LockedFeatureButton({ featureName, children, size = "default", variant = "outline" }) {
  const requiredPlan = getRequiredPlan(featureName);
  
  return (
    <div className="relative inline-block group">
      <Button 
        disabled 
        size={size}
        variant={variant}
        className="opacity-60 cursor-not-allowed"
      >
        {children}
        <Lock className="w-3 h-3 ml-2" />
      </Button>
      <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 hidden group-hover:block z-50">
        <div className="bg-gray-900 text-white text-xs rounded py-2 px-3 whitespace-nowrap">
          <p className="font-semibold">Requires {requiredPlan} Plan</p>
          <p className="text-gray-300 mt-1">Contact admin to upgrade</p>
        </div>
        <div className="absolute top-full left-1/2 transform -translate-x-1/2 -mt-1">
          <div className="border-4 border-transparent border-t-gray-900"></div>
        </div>
      </div>
    </div>
  );
}

/**
 * Locked Feature Badge - Shows a badge indicating feature is locked
 */
export function LockedFeatureBadge({ featureName }) {
  const requiredPlan = getRequiredPlan(featureName);
  
  return (
    <Badge variant="secondary" className="ml-2">
      <Lock className="w-3 h-3 mr-1" />
      {requiredPlan}+
    </Badge>
  );
}

/**
 * Locked Feature Section - Shows a message for locked sections
 */
export function LockedFeatureSection({ featureName, title }) {
  const requiredPlan = getRequiredPlan(featureName);
  
  return (
    <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
      <Lock className="w-12 h-12 mx-auto text-gray-400 mb-4" />
      <h3 className="text-lg font-semibold mb-2">{title}</h3>
      <p className="text-gray-600 mb-4">
        This feature requires the <strong>{requiredPlan}</strong> plan or higher
      </p>
      <Button variant="default" onClick={() => window.location.href = '/upgrade'}>
        Upgrade Plan
      </Button>
    </div>
  );
}

/**
 * Feature Gate Wrapper - Conditionally renders children or locked state
 */
export function FeatureGate({ user, featureName, children, fallback = null }) {
  const { hasFeature } = require('@/utils/features');
  
  if (hasFeature(user, featureName)) {
    return children;
  }
  
  if (fallback) {
    return fallback;
  }
  
  return <LockedFeatureSection featureName={featureName} title="Feature Locked" />;
}
